import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useGetPypestreamUserIdQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { DatadogFlag, useDatadogFlag } from 'state/datadog-flags';
import { withForterHeaders } from 'state/graphql/links/with-forter-headers';
import { useLocationContext } from 'state/location';
import { brand as brandShort, env, fullBrandName, getConfigValue } from 'utils/environment';
import { routes } from 'utils/routing';

declare global {
  interface Window {
    Pypestream?: any;
  }
}

export const PYPESTREAM_SCRIPT_ID = 'pypestream-script';
export const PYPESTREAM_INTERFACE_ID = 'pypestream-interface';
const stage = env();

const defaultContext = {
  show: () => {},
  hide: () => {},
  declareContainerReady: () => {},
  injectScript: () => {},
  visible: false,
  loading: true,
  loadingMessage: '',
};

const PypestreamContext = createContext(defaultContext);

export const usePypestreamContext = () => useContext(PypestreamContext);

const { scriptUrl, appId } = getConfigValue('pypestream');

const brand = fullBrandName();
const shortBrand = brandShort();

type PypestreamIdentity =
  | { kind: 'pending' }
  | { kind: 'guest' }
  | { kind: 'authenticated'; pypestreamUserId: string };

type BootableIdentity = Exclude<PypestreamIdentity, { kind: 'pending' }>;

const pypestreamIdentity = (
  authLoading: boolean,
  signedIn: boolean,
  queryLoading: boolean,
  queryError: boolean,
  pypestreamUserId: string
): PypestreamIdentity => {
  if (authLoading || (signedIn && queryLoading)) {
    return { kind: 'pending' };
  }
  if (!signedIn || queryError || !pypestreamUserId) {
    return { kind: 'guest' };
  }
  return { kind: 'authenticated', pypestreamUserId };
};

const bootKeyFor = (identity: PypestreamIdentity) => {
  if (identity.kind === 'authenticated') {
    return `auth:${identity.pypestreamUserId}`;
  }
  return identity.kind;
};

const bootPypestream = (
  identity: BootableIdentity,
  welcomeMessage: string,
  onMount: () => void
) => {
  const passthrough = [
    { attribute: 'stage', label: 'stage', value: stage, visible: 'wpm' },
    {
      attribute: 'forterDeviceId',
      label: 'forterDeviceId',
      value: withForterHeaders?.deviceId ?? '',
      visible: 'wpm',
    },
    {
      attribute: 'forterToken',
      label: 'forterToken',
      value: withForterHeaders?.ttiForterToken ?? '',
      visible: 'wpm',
    },
    { attribute: 'userAgent', label: 'userAgent', value: navigator.userAgent, visible: 'wpm' },
  ];
  if (identity.kind === 'authenticated') {
    passthrough.push({
      attribute: 'pypestreamUserId',
      label: 'pypestreamUserId',
      value: identity.pypestreamUserId,
      visible: 'wpm',
    });
  }
  window?.Pypestream?.boot(
    {
      APP_ID: appId,
      disableWidgetSlideInOutAnimation: true,
      initialMessages: {
        isPersistent: true,
        showNextThinkingBubble: true,
        welcomeMessage,
      },
      language: 'en',
      passthrough: JSON.stringify(passthrough),
      play_chime: false,
      token: '',
      withInlineAppLoader: false,
    },
    document.getElementById(PYPESTREAM_INTERFACE_ID)
  );
  window?.Pypestream?.onMount(onMount);
};

export const PypestreamProvider = ({ children }: IBaseProps) => {
  const { location } = useLocationContext();

  const [visible, setVisible] = useState(false);
  const [containerReady, setContainerReady] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const bootedKeyRef = useRef<string | null>(null);
  const { formatMessage } = useIntl();

  const { user, loading: authLoading } = useAuthContext();
  const enablePypestream = useDatadogFlag(DatadogFlag.ENABLE_PYPESTREAM_WEB);
  const signedIn = Boolean(user);
  const {
    data,
    loading: pypestreamUserIdLoading,
    error: pypestreamUserIdError,
  } = useGetPypestreamUserIdQuery({
    skip: !signedIn || !enablePypestream,
    fetchPolicy: 'cache-and-network',
  });
  const identity = pypestreamIdentity(
    authLoading,
    signedIn,
    pypestreamUserIdLoading,
    Boolean(pypestreamUserIdError),
    data?.me?.pypestreamUserId ?? ''
  );
  const bootKey = bootKeyFor(identity);
  const authenticatedId = identity.kind === 'authenticated' ? identity.pypestreamUserId : '';
  const loading = location.pathname?.startsWith(routes.support) && !mounted;

  const name = user?.details?.name ?? '';

  const welcomeMessage = formatMessage(
    { id: `pypestreamWelcome${name ? 'User' : 'Anon'}` },
    { brand, name }
  );
  const loadingMessage = formatMessage({ id: `${shortBrand}.pypestreamLoadingMessage` });

  const declareContainerReady = useCallback(() => setContainerReady(true), []);

  const show = useCallback(() => {
    setVisible(true);
    window?.Pypestream?.show();
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    window?.Pypestream?.hide();
  }, []);

  const injectScript = useCallback(() => {
    if (!document.getElementById(PYPESTREAM_SCRIPT_ID) && enablePypestream) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `${scriptUrl}?ts=${Date.now()}`;
      script.async = true;
      script.id = PYPESTREAM_SCRIPT_ID;
      script.dataset.testid = PYPESTREAM_SCRIPT_ID;
      script.onload = () => setScriptReady(true);
      document.body.appendChild(script);
    }
  }, [enablePypestream]);

  useEffect(() => {
    // The encrypted id arrives after auth. Booting as a guest first would open chat twice.
    if (!scriptReady || !containerReady || bootKey === 'pending') {
      return;
    }
    if (bootedKeyRef.current === bootKey) {
      return;
    }
    if (bootedKeyRef.current !== null) {
      window?.Pypestream?.shutdown();
    }
    const readyIdentity: BootableIdentity = authenticatedId
      ? { kind: 'authenticated', pypestreamUserId: authenticatedId }
      : { kind: 'guest' };
    bootPypestream(readyIdentity, welcomeMessage, () => setMounted(true));
    bootedKeyRef.current = bootKey;
    // The welcome message is read at boot only: a name change must not restart an open chat.
  }, [authenticatedId, bootKey, containerReady, scriptReady, welcomeMessage]);

  return (
    <PypestreamContext.Provider
      value={{ show, hide, visible, declareContainerReady, injectScript, loading, loadingMessage }}
    >
      {children}
    </PypestreamContext.Provider>
  );
};
